import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3f89a364"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "upload-tip" }, "仅支持上传 1 个 Excel 文件（.xlsx / .xls）", -1))
const _hoisted_2 = {
  key: 0,
  class: "import-empty-tip"
}

import { computed, reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { UploadOutlined } from '@ant-design/icons-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import type { UploadProps } from 'ant-design-vue';
import {
  createImportTask,
  fetchImportTemplateUrl,
  queryImportTemplates,
  RECEIVABLE_MANAGE_EXPORT_MODEL,
  type ExcelWorkbookDefinitionItem,
} from './index';
import {
  uploadFileToObsResource,
  type UploadedResourceFile,
} from '../receivableCustomer/obsUpload';


export default /*#__PURE__*/_defineComponent({
  __name: 'ImportDialog',
  props: {
    visible: { type: Boolean },
    model: null,
    path: null,
    masterIdList: null,
    queryData: null
  },
  emits: ["update:visible", "success"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:visible', value: boolean): void;
  (e: 'success'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  visible: boolean;
  model?: string;
  /** 导入请求 variables.path */
  path?: string;
  /** 勾选行 masterId；有值时模板只填勾选数据 */
  masterIdList?: string[];
  /** 未勾选时传入当前列表筛选条件 */
  queryData?: Record<string, unknown>;
}





const loading = ref(false);
const submitting = ref(false);
const downloading = ref(false);
const uploading = ref(false);
const formRef = ref<FormInstance>();
const templates = ref<ExcelWorkbookDefinitionItem[]>([]);
const fileList = ref<UploadProps['fileList']>([]);
const uploadedFile = ref<UploadedResourceFile | null>(null);

const form = reactive({
  selectedTemplateId: undefined as string | undefined,
  /** 用于表单必填校验：有上传成功文件时写入 id */
  fileId: '' as string,
});

/** 对齐设计器：字段下方红字「必填」 */
const formRules: Record<string, Rule[]> = {
  selectedTemplateId: [{ required: true, message: '必填', trigger: 'change' }],
  fileId: [{ required: true, message: '必填', trigger: 'change' }],
};

const FIELD_LABELS: Record<string, string> = {
  selectedTemplateId: '导入模板',
  fileId: '上传文件',
};

const templateOptions = computed(() =>
  templates.value.map((item) => ({
    value: item.id,
    label: item.displayName || item.id,
  })),
);

function filterTemplateOption(input: string, option?: { label?: string }) {
  const keyword = input.trim().toLowerCase();
  if (!keyword) return true;
  return String(option?.label ?? '')
    .toLowerCase()
    .includes(keyword);
}

function showApiError(error: unknown, fallback: string) {
  const content = error instanceof Error ? error.message : fallback;
  message.error({
    content,
    class: 'receivable-api-error-msg',
    duration: 5,
  });
}

function showRequiredTip(label: string) {
  message.error(`${label} 必填`);
}

function resetUploadState() {
  fileList.value = [];
  uploadedFile.value = null;
  form.fileId = '';
}

async function loadTemplates() {
  loading.value = true;
  form.selectedTemplateId = undefined;
  templates.value = [];
  resetUploadState();
  formRef.value?.clearValidate();
  try {
    templates.value = await queryImportTemplates(
      props.model || RECEIVABLE_MANAGE_EXPORT_MODEL,
    );
    if (templates.value.length === 1) {
      form.selectedTemplateId = templates.value[0].id;
    }
  } catch (error) {
    showApiError(error, '查询导入模板失败');
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.visible,
  (open) => {
    if (open) loadTemplates();
  },
);

function handleCancel() {
  emit('update:visible', false);
}

function handleRemoveFile() {
  resetUploadState();
  formRef.value?.validateFields(['fileId']).catch(() => undefined);
  return true;
}

const beforeUpload: UploadProps['beforeUpload'] = async (file) => {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
    message.error({
      content: '请上传 Excel 文件（.xlsx / .xls）',
      class: 'receivable-api-error-msg',
    });
    return false;
  }

  uploading.value = true;
  resetUploadState();
  fileList.value = [
    {
      uid: String(file.uid ?? Date.now()),
      name: file.name,
      status: 'uploading',
      percent: 50,
    },
  ];

  try {
    // 顺序：签名 → OBS → 登记 ResourceFile（拿到 id/url）
    const resource = await uploadFileToObsResource(file as File);
    uploadedFile.value = resource;
    form.fileId = resource.id;
    fileList.value = [
      {
        uid: String(file.uid ?? Date.now()),
        name: file.name,
        status: 'done',
        url: resource.url,
      },
    ];
    formRef.value?.clearValidate(['fileId']);
  } catch (error) {
    resetUploadState();
    showApiError(error, '文件上传失败');
  } finally {
    uploading.value = false;
  }

  return false;
};

async function handleDownloadTemplate() {
  try {
    await formRef.value?.validateFields(['selectedTemplateId']);
  } catch {
    showRequiredTip('导入模板');
    return;
  }

  downloading.value = true;
  try {
    const redirectUri = await fetchImportTemplateUrl(form.selectedTemplateId!, {
      masterIdList: props.masterIdList,
      queryData: props.queryData,
    });
    // 接口是异步的，target=_blank 容易被浏览器拦截，同页触发下载
    const link = document.createElement('a');
    link.href = redirectUri;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    showApiError(error, '下载模板失败');
  } finally {
    downloading.value = false;
  }
}

async function handleImport() {
  try {
    await formRef.value?.validate();
  } catch (err: unknown) {
    const errorFields = (err as { errorFields?: Array<{ name: Array<string | number> }> })
      ?.errorFields;
    const firstName = errorFields?.[0]?.name?.[0];
    const label = firstName != null ? FIELD_LABELS[String(firstName)] : '';
    showRequiredTip(label || '必填项');
    return;
  }

  if (uploading.value) {
    message.warning('文件上传中，请稍候');
    return;
  }

  if (!uploadedFile.value?.id || !uploadedFile.value?.url) {
    showRequiredTip('上传文件');
    return;
  }

  submitting.value = true;
  try {
    const result = await createImportTask({
      workbookDefinitionId: form.selectedTemplateId!,
      fileId: uploadedFile.value.id,
      fileUrl: uploadedFile.value.url,
      path: props.path,
    });

    if (String(result.state ?? '').toUpperCase() === 'FAILURE') {
      message.error({
        content: result.errorMessage || '导入失败',
        class: 'receivable-api-error-msg',
        duration: 5,
      });
      return;
    }

    emit('success');
    emit('update:visible', false);
    message.success('导入成功');
  } catch (error) {
    showApiError(error, '导入失败');
  } finally {
    submitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_upload = _resolveComponent("a-upload")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createBlock(_component_a_modal, {
    visible: __props.visible,
    title: "导入",
    width: 520,
    "mask-closable": false,
    "destroy-on-close": "",
    "confirm-loading": submitting.value,
    "wrap-class-name": "import-dialog-modal",
    onCancel: handleCancel
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_a_button, {
        loading: downloading.value,
        onClick: handleDownloadTemplate
      }, {
        default: _withCtx(() => [
          _createTextVNode("下载模板")
        ]),
        _: 1
      }, 8, ["loading"]),
      _createVNode(_component_a_button, {
        type: "primary",
        loading: submitting.value,
        onClick: handleImport
      }, {
        default: _withCtx(() => [
          _createTextVNode("导入")
        ]),
        _: 1
      }, 8, ["loading"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_a_spin, { spinning: loading.value }, {
        default: _withCtx(() => [
          _createVNode(_component_a_form, {
            ref_key: "formRef",
            ref: formRef,
            layout: "vertical",
            class: "import-dialog-form",
            model: form,
            rules: formRules
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_form_item, {
                label: "导入模板",
                name: "selectedTemplateId"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_select, {
                    value: form.selectedTemplateId,
                    "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.selectedTemplateId) = $event)),
                    "show-search": "",
                    "allow-clear": "",
                    placeholder: "请选择导入模板",
                    options: _unref(templateOptions),
                    "filter-option": filterTemplateOption,
                    style: {"width":"100%"}
                  }, null, 8, ["value", "options"])
                ]),
                _: 1
              }),
              _createVNode(_component_a_form_item, {
                label: "上传文件",
                name: "fileId"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_upload, {
                    "file-list": fileList.value,
                    "before-upload": beforeUpload,
                    "max-count": 1,
                    accept: ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
                    disabled: uploading.value,
                    onRemove: handleRemoveFile
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_button, { loading: uploading.value }, {
                        icon: _withCtx(() => [
                          _createVNode(_unref(UploadOutlined))
                        ]),
                        default: _withCtx(() => [
                          _createTextVNode(" 点击上传 ")
                        ]),
                        _: 1
                      }, 8, ["loading"])
                    ]),
                    _: 1
                  }, 8, ["file-list", "disabled"]),
                  _hoisted_1
                ]),
                _: 1
              }),
              (!loading.value && _unref(templateOptions).length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, " 暂无可用导入模板，请先在后台配置后再试 "))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["model"])
        ]),
        _: 1
      }, 8, ["spinning"])
    ]),
    _: 1
  }, 8, ["visible", "confirm-loading"]))
}
}

})